import { useNavigate } from "react-router";
import { useTenant } from "../../Contexts/TenantContext";
import { Order } from "../../Models/Order";
import { ShippingOptionResponse } from "../../Models/Shipping";
import { CustomerType } from "../../Enums/CustomerType";
import { ShippingMethod } from "../../Enums/ShippingMethod";
import { CartItem } from "../../Types/CartItemType";
import {
  formatCnpj,
  formatCpf,
  formatDateToBR,
  formatPhoneNumber,
  formatPostalCode,
  formatPriceBRL
} from "../../Utils/DataFormatters";
import { getOrderStatusLabel, getPaymentMethodLabel, getShippingMethodLabel } from "../../Utils/Enums";
import { FaEye } from "react-icons/fa";
import { PaymentMethod } from "../../Enums/PaymentMethod";

type ReviewStepProps = {
  cartItems?: CartItem[] | null;
  selectedAddress: string | null;
  selectedShipping: ShippingOptionResponse | null;
  isOrderPage?: boolean;
  isAdmin?: boolean;
  isOperator?: boolean;
  order?: Order | null;
};

const ReviewStep = ({
  cartItems,
  selectedAddress,
  selectedShipping,
  isOrderPage = false,
  isAdmin = false,
  isOperator = false,
  order
}: ReviewStepProps) => {
  const navigate = useNavigate();

  const { tenantContact, tenantPreference } = useTenant();
  const hideProductCode = tenantPreference?.autoGenerateProductCode === true && !isAdmin && !isOperator;

  const isCustomerIP = order?.customer?.customerType === CustomerType.IndividualPerson;

  const shippingMethodType = selectedShipping?.methodType ?? order?.shippingMethod ?? null;
  const isStorePickup = shippingMethodType === ShippingMethod.StorePickup;
  const isCustomCarrier = shippingMethodType === ShippingMethod.CustomCarrier;
  const shippingInfoLabel = isStorePickup ? "Informações de retirada:" : "Informações de entrega:";
  const timeEstimateLabel = isStorePickup ? "Estimativa de retirada" : "Estimativa de entrega";
  const shippingMethodLabel = shippingMethodType !== null
    ? getShippingMethodLabel(shippingMethodType)
    : "Nenhuma opção selecionada.";
  const shippingDetails = order?.shippingDetails;
  const shouldShowShippingDetails = shippingDetails != null;
  const isCompanyDelivery = shippingDetails?.isCompanyDelivery ?? false;
  const isCorreiosShipping =
    shippingMethodType === ShippingMethod.CorreiosPAC ||
    shippingMethodType === ShippingMethod.CorreiosSedex;
  const getDaySuffix = (days?: number | null) => (days === 1 ? "dia" : "dias");
  const estimatedDeliveryDateText = shippingDetails?.estimatedDeliveryDate
    ? formatDateToBR(shippingDetails.estimatedDeliveryDate)
    : null;

  return (
    <div className={`flex flex-col bg-white border p-4 gap-5 flex-[1.6] rounded shadow`}>
      {isOrderPage && order ? (
        <h2 className="text-lg font-bold text-primary-600">{"Pedido"} #{order.orderNumber}</h2>
      ) : (
        <h2 className="text-lg font-bold">{"Revisar pedido"}</h2>
      )}

      {isOrderPage && order && (
        <>
          <div>
            <p className="font-semibold mb-1">{"Data do pedido"}:</p>
            <p>{formatDateToBR(order.createdAt)}</p>
          </div>

          {(isAdmin || isOperator) && (
            <div className="gap-2 sm:gap-1">
              <p className="font-semibold">{"Cliente"}:</p>
              <div className="mb-1">
                <p>{"Tipo"}: {isCustomerIP ? "Pessoa Física" : "Pessoa Jurídica"}</p>
              </div>
              <div className="mb-1">
                <p>{isCustomerIP ? "Nome" : "Razão Social"}: {order.customer?.fullNameOrCompanyName}</p>
              </div>
              <div className="mb-1">
                <p>{isCustomerIP ? "CPF" : "CNPJ"}: {isCustomerIP ? formatCpf(order.customer?.cpfOrCnpj!) : formatCnpj(order.customer?.cpfOrCnpj!)}</p>
              </div>
              <div className="mb-1">
                <p>{"E-mail"}: {order.customer?.email}</p>
              </div>
              <div className="mb-1">
                <p>{"Telefone celular"}: {formatPhoneNumber(order.customer?.phoneNumber!)}</p>
              </div>
              <button
                onClick={() => navigate(`${isAdmin ? "/admin" : "/operador"}/clientes/${order.customerId}`)}
                className="flex gap-1 items-center text-primary-600 hover:underline"
              >
                <FaEye />
                {"Ver mais informações"}
              </button>
            </div>
          )}

          <div>
            <p className="font-semibold mb-1">{"Status"}:</p>
            <p>{getOrderStatusLabel(order.status)}</p>
          </div>
        </>
      )}

      {!isStorePickup && (
        <div>
          <p className="font-semibold mb-1">{"Endereço de entrega"}:</p>
          <p>{selectedAddress || "Nenhum endereço selecionado."}</p>
        </div>
      )}

      <div>
        <p className="font-semibold mb-1">{"Método de entrega"}:</p>
        <p>{shippingMethodLabel}</p>
      </div>

      {shouldShowShippingDetails && shippingDetails && (
        <div className="flex flex-col gap-1">
          <p className="font-semibold">{shippingInfoLabel}</p>
          {isStorePickup ? (
            <>
              <div className="flex gap-1">
                <p>
                  {"Endereço"}: {(() => {
                    const loc = order?.pickedUpAtBranch ?? tenantContact;
                    return `${loc.street}, ${loc.number} - ${loc.neighborhood}, ${loc.city}/${loc.state} - ${formatPostalCode(loc.postalCode)}${loc.complement ? ` - ${loc.complement}` : ""}`;
                  })()}
                </p>
              </div>
              {estimatedDeliveryDateText ? (
                <div className="flex gap-1">
                  <p>{"Entrega estimada para"}: {estimatedDeliveryDateText}</p>
                </div>
              ) : shippingDetails.estimatedTimeInDays != null && (
                <div className="flex gap-1">
                  <p>{timeEstimateLabel}: {shippingDetails.estimatedTimeInDays} {getDaySuffix(shippingDetails.estimatedTimeInDays)}</p>
                </div>
              )}
            </>
          ) : (
            <>
              {isCompanyDelivery ? (
                <div className="flex gap-1">
                  <p>{"Entrega"}: {"Transporte próprio"}</p>
                </div>
              ) : isCustomCarrier && (
                <>
                  {shippingDetails.carrierName && (
                    <div className="flex gap-1">
                      <p>{"Nome da transportadora"}: {shippingDetails.carrierName}</p>
                    </div>
                  )}
                  {shippingDetails.carrierCnpj && (
                    <div className="flex gap-1">
                      <p>{"CNPJ da transportadora"}: {formatCnpj(shippingDetails.carrierCnpj)}</p>
                    </div>
                  )}
                </>
              )}
              {shippingDetails.price != null && (
                <div className="flex gap-1">
                  <p>{"Valor do frete"}: {formatPriceBRL(shippingDetails.price)}</p>
                  {shippingDetails.isSenderResponsible && (
                    <p>({"FRETE GRÁTIS"})</p>
                  )}
                </div>
              )}
              {estimatedDeliveryDateText ? (
                <div className="flex gap-1">
                  <p>{"Entrega estimada para"}: {estimatedDeliveryDateText}</p>
                </div>
              ) : shippingDetails.estimatedTimeInDays != null && (
                <div className="flex gap-1">
                  <p>{timeEstimateLabel}: {shippingDetails.estimatedTimeInDays} {getDaySuffix(shippingDetails.estimatedTimeInDays)}</p>
                </div>
              )}
              {!isCompanyDelivery && isCorreiosShipping && shippingDetails.trackingCode && (
                <div className="flex gap-1">
                  <p>{"Código de rastreio"}: {shippingDetails.trackingCode}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isOrderPage && order?.paymentMethod != null && (
        <div className="flex flex-col gap-5">
          <div>
            <p className="font-semibold mb-1">{"Pagamento via"}:</p>
            <p>
              {getPaymentMethodLabel(order.paymentMethod)}
              {order.paymentMethod === PaymentMethod.CreditCard &&
                order.creditCardInstallments &&
                order.creditCardInstallments > 1 &&
                ` (${order.creditCardInstallments}x ${order.creditCardInterest && order.creditCardInterest > 0 ? "com juros" : "sem juros"})`}
            </p>
          </div>
          {order.paymentReceivedAt && (
            <div>
              <p className="font-semibold mb-1">{"Pagamento realizado em"}:</p>
              <p>{formatDateToBR(order.paymentReceivedAt)}</p>
            </div>
          )}
        </div>
      )}

      <div>
        <p className="font-semibold mb-2">{isOrderPage ? "Itens do pedido:" : "Itens do carrinho:"}</p>
        <ul>
          {isOrderPage && order?.orderProducts ? (
            order.orderProducts.map((item) => (
              <li key={item.id} className="py-2 px-3 border">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">{item.product.name}</span>
                  {item.size && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                      {item.size}
                    </span>
                  )}
                </div>
                {!hideProductCode && item.product.code && <p className="text-xs text-gray-600">{"CÓD."} {item.product.code}</p>}
                {item.product.isMadeToOrder && (
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] bg-primary-50 text-primary-700 border border-primary-200">
                      {"Encomenda"}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm">{"Quantidade"}: {item.quantity}</span>
                  <span>{formatPriceBRL(item.totalPrice)}</span>
                </div>
              </li>
            ))
          ) : (
            cartItems?.map((item) => (
              <li key={`${item.id}:${item.size ?? ""}`} className="py-2 px-3 border">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">{item.name}</span>
                  {item.size && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                      {item.size}
                    </span>
                  )}
                </div>
                {!hideProductCode && item.code && <p className="text-xs text-gray-600">{"CÓD."} {item.code}</p>}
                {item.isMadeToOrder && (
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] bg-primary-50 text-primary-700 border border-primary-200">
                      {"Encomenda"}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm">{"Quantidade"}: {item.quantity}</span>
                  <span>{formatPriceBRL(item.price * item.quantity)}</span>
                </div>                
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default ReviewStep;
